import { useCallback, useEffect, useState } from "react";
import { AdminLayout } from "../../components/Layouts";
import { api, readUser, fmt, errMsg } from "../../lib/api";
import { Check, X, Radar, Loader2, ExternalLink, Clock, Link as LinkIcon } from "lucide-react";

const TX_HASH_RE = /^0x[0-9a-fA-F]{64}$/;

function bscScanHref(d, wallet) {
  const tx = (d.transaction_hash || "").trim();
  if (TX_HASH_RE.test(tx)) return `https://bscscan.com/tx/${tx}`;
  if (wallet) return `https://bscscan.com/address/${wallet}`;
  return null;
}

function formatCountdown(reservedUntil, nowMs) {
  if (!reservedUntil) return null;
  const ts = Date.parse(reservedUntil);
  if (Number.isNaN(ts)) return null;
  const diff = ts - nowMs;
  if (diff <= 0) return { expired: true, label: "Süresi doldu" };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return { expired: false, label: `${h}s ${m}d kaldı` };
}

export default function AdminDeposits() {
  const user = readUser();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [notice, setNotice] = useState("");
  const [noticeKind, setNoticeKind] = useState("error");
  const [scanning, setScanning] = useState(false);
  const [wallet, setWallet] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());
  const [monitorStatus, setMonitorStatus] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/deposits", { params: filter ? { status: filter } : {} });
    setRows(r.data);
  }, [filter]);
  const loadMonitorStatus = useCallback(async () => {
    const r = await api.get("/admin/monitor/status");
    setMonitorStatus(r.data || null);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => { loadMonitorStatus().catch(() => {}); }, [loadMonitorStatus]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setWallet((r.data || {}).deposit_wallet || "")).catch(() => {});
  }, []);

  // Tick every 30s so countdowns stay fresh without hammering render
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  async function review(hash, action) {
    setNotice(""); setNoticeKind("error");
    try { await api.post(`/admin/deposits/${hash}/${action}`); load(); } catch (e) { setNotice(errMsg(e)); }
  }

  async function attachTx(currentHash) {
    const txHash = window.prompt(
      "BscScan'den aldığın gerçek TX hash'ini yapıştır (0x ile başlar, 64 karakter):\n\nSistem on-chain receipt'i okur, bu depozitonun beklenen tutarıyla eşleşen Transfer logunu bulur ve otomatik aktive eder."
    );
    if (!txHash) return;
    setNotice(""); setNoticeKind("success");
    try {
      const r = await api.post(`/admin/deposits/${currentHash}/attach-tx`, { transaction_hash: txHash.trim() });
      setNotice(`TX ${r.data.transaction_hash.slice(0, 14)}… bağlandı · $${Number(r.data.amount).toFixed(2)} doğrulandı · paket aktif edildi.`);
      load();
    } catch (e) { setNoticeKind("error"); setNotice(errMsg(e)); }
  }

  async function runScan() {
    setScanning(true); setNotice(""); setNoticeKind("success");
    try {
      const r = await api.post("/admin/monitor/run?deep=true");
      const { verified = 0, matched = 0, pending = 0, awaiting_remaining = 0, expired = 0, logs_found = 0, rpc_errors = 0, skipped, error } = r.data || {};
      if (skipped === "busy") setNotice(`Tarama zaten çalışıyor; aynı anda ikinci tarama başlatılmadı.`);
      else if (skipped) setNotice(`Tarama atlandı — platform SETUP modunda veya cüzdan tanımsız.`);
      else if (error) { setNoticeKind("error"); setNotice(`RPC hatası: ${error}`); }
      else if (rpc_errors) { setNoticeKind("error"); setNotice(`BSC taraması kısmi tamamlandı · ${logs_found} zincir kaydı bulundu, ${rpc_errors} RPC aralığı başarısız. Cursor başarısız aralığın ötesine geçirilmedi.`); }
      else setNotice(`BSC taraması tamam · ${logs_found} zincir kaydı, ${matched} otomatik eşleşme, ${verified} doğrulandı, ${pending} TX hash incelendi, ${expired} rezervasyon süresi doldu, ${awaiting_remaining} halen bekliyor.`);
      await Promise.all([load(), loadMonitorStatus()]);
    } catch (e) { setNoticeKind("error"); setNotice(errMsg(e)); }
    finally { setScanning(false); }
  }

  const filters = ["", "Awaiting Transfer", "Pending", "Verified", "Confirmed", "Expired", "Rejected"];
  return (
    <AdminLayout user={user} active="Depozitolar">
      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">İŞLEM KUYRUĞU</span><h2>Depozitolar</h2></div>
          <div className="filter-pills" style={{ alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-accent"
              style={{ padding: "10px 16px", fontSize: 12 }}
              onClick={runScan}
              disabled={scanning}
              data-testid="admin-bsc-scan-button"
              title="Sistem her 15 dakikada otomatik tarama yapıyor. Buton acil manuel derin tarama içindir."
            >
              {scanning ? <Loader2 size={14} className="spin" /> : <Radar size={14} />} {scanning ? "Taranıyor…" : "BSC Tara (Oto · 15 dk)"}
            </button>
            {filters.map((f) => <button key={f || "all"} className={filter === f ? "pill active" : "pill"} onClick={() => setFilter(f)} data-testid={`admin-dep-filter-${(f || "all").toLowerCase().replace(/\s/g, "-")}`}>{f || "Tümü"}</button>)}
          </div>
        </div>
        {monitorStatus && (
          <div className={monitorStatus.healthy ? "success" : "error"} data-testid="admin-monitor-status">
            Otomatik tarama: <strong>{monitorStatus.healthy ? "sağlıklı" : "kontrol gerekli"}</strong>
            {` · Her ${monitorStatus.schedule_minutes} dk · Son blok ${Number(monitorStatus.last_scanned_block || 0).toLocaleString("tr-TR")}`}
            {monitorStatus.last_run?.at && ` · Son çalışma ${new Date(monitorStatus.last_run.at).toLocaleString("tr-TR")}`}
          </div>
        )}
        {notice && <div className={noticeKind === "success" ? "success" : "error"} data-testid="admin-scan-notice">{notice}</div>}
        <div className="table-wrap">
          <table className="admin-deposits-table">
            <thead><tr><th>Kullanıcı</th><th>Referans</th><th>Paket</th><th>Tutar</th><th>TX Hash</th><th>Tarih</th><th>Durum</th><th>İşlem</th></tr></thead>
            <tbody>
              {rows.map((d, i) => {
                const href = bscScanHref(d, wallet);
                const countdown = d.awaiting_match && d.reserved_until ? formatCountdown(d.reserved_until, nowMs) : null;
                return (
                  <tr key={d.transaction_hash}>
                    <td data-label="Kullanıcı">{d.user_email}</td>
                    <td data-label="Referans" className="hash">{d.deposit_reference || "—"}</td>
                    <td data-label="Paket">{d.package_name}</td>
                    <td data-label="Tutar" data-testid={`admin-dep-amount-${i}`}>
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="amount-link" data-testid={`admin-dep-bscscan-${i}`} title="BscScan'de aç">
                          ${fmt(d.expected_amount ?? d.amount)} <ExternalLink size={11} />
                        </a>
                      ) : (
                        <>${fmt(d.expected_amount ?? d.amount)}</>
                      )}
                    </td>
                    <td data-label="TX Hash" className="tx-cell">
                      {d.awaiting_match ? (
                        <em>otomatik beklemede</em>
                      ) : TX_HASH_RE.test(d.transaction_hash || "") ? (
                        <div className="tx-info" data-testid={`admin-tx-info-${i}`}>
                          <a href={`https://bscscan.com/tx/${d.transaction_hash}`} target="_blank" rel="noopener noreferrer" className="amount-link" data-testid={`admin-tx-link-${i}`}>
                            {d.transaction_hash.slice(0, 10)}…{d.transaction_hash.slice(-6)} <ExternalLink size={11} />
                          </a>
                          {typeof d.confirmations === "number" && d.confirmations > 0 && (
                            <small className="tx-conf" data-testid={`admin-tx-conf-${i}`}>{d.confirmations.toLocaleString("tr-TR")} onay</small>
                          )}
                          {d.attached_by_admin && <small className="tx-attached" title={`Manuel bağlandı: ${d.attached_by_admin}`}>manuel bağlı</small>}
                        </div>
                      ) : (
                        <span className="hash">{(d.transaction_hash || "").slice(0, 16)}…</span>
                      )}
                    </td>
                    <td data-label="Tarih">
                      <div>{new Date(d.created_at).toLocaleString("tr-TR")}</div>
                      {countdown && (
                        <small className={`countdown ${countdown.expired ? "expired" : ""}`} data-testid={`admin-countdown-${i}`}>
                          <Clock size={10} /> {countdown.label}
                        </small>
                      )}
                    </td>
                    <td data-label="Durum"><span className={`status ${d.status.toLowerCase().replace(/\s/g, "-")}`}>{d.status}</span></td>
                    <td data-label="İşlem">{(d.status === "Pending" || d.status === "Verified" || d.status === "Awaiting Transfer") ? (<>
                      {d.awaiting_match && <button className="table-action attach-tx" data-testid={`admin-attach-tx-${i}`} onClick={() => attachTx(d.transaction_hash)} title="BscScan'den gerçek TX hash yapıştır"><LinkIcon size={13} /></button>}
                      <button className="table-action approve" data-testid={`admin-approve-${i}`} onClick={() => review(d.transaction_hash, "approve")}><Check size={14} /></button>
                      <button className="table-action reject" data-testid={`admin-reject-${i}`} onClick={() => review(d.transaction_hash, "reject")}><X size={14} /></button>
                    </>) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <div className="table-empty">Bu filtre için depozito yok.</div>}
        </div>
      </section>
    </AdminLayout>
  );
}
